/**
 * useMarketWS — connects to the backend /api/v1/market/ws/prices endpoint
 * and delivers real-time price ticks for FOREX/CC symbols (event-driven)
 * and polled updates for JSE/US symbols.
 *
 * Preferred over the direct EODHD WebSocket service because:
 * - The EODHD API key is not exposed in browser JavaScript
 * - Connections are authenticated via JWT
 * - Includes optional indicator updates (RSI, SMA)
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const API_WS_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001')
  .replace(/^http/, 'ws');

export interface MarketTick {
  symbol: string;
  price: number;
  bid?: number;
  ask?: number;
  change: number;
  change_pct: number;
  timestamp: string;
}

export interface IndicatorSnapshot {
  symbol: string;
  rsi_14?: number;
  sma_50?: number;
  sma_200?: number;
  cached_at: string;
}

interface UseMarketWSOptions {
  symbols: string[];
  includeIndicators?: boolean;
  pollInterval?: number; // seconds, for JSE/US (default 30)
}

interface UseMarketWSResult {
  ticks: Record<string, MarketTick>;
  indicators: Record<string, IndicatorSnapshot>;
  connected: boolean;
  error: string | null;
}

const BACKOFF_DELAYS = [500, 1000, 2000, 4000, 8000, 15000, 30000];

/** Minimum ms between React state flushes — prevents per-tick re-renders on fast FOREX/CC streams. */
const TICK_FLUSH_INTERVAL_MS = 400;

export function useMarketWS({
  symbols,
  includeIndicators = false,
  pollInterval = 30,
}: UseMarketWSOptions): UseMarketWSResult {
  const [ticks, setTicks] = useState<Record<string, MarketTick>>({});
  const [indicators, setIndicators] = useState<Record<string, IndicatorSnapshot>>({});
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  const symbolsKey = symbols.join(',');

  // Pending ticks buffer — incoming messages are accumulated here and flushed
  // to React state at most once per TICK_FLUSH_INTERVAL_MS. This prevents
  // per-message re-renders on fast FOREX/CC streams (which can tick many
  // times per second), eliminating the rapid visual flashing on Exchange tabs.
  const pendingTicksRef = useRef<Record<string, MarketTick>>({});
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushPendingTicks = useCallback(() => {
    flushTimerRef.current = null;
    const pending = pendingTicksRef.current;
    if (Object.keys(pending).length === 0) return;
    pendingTicksRef.current = {};
    setTicks((prev) => ({ ...prev, ...pending }));
  }, []);

  const disconnect = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      ws.close();
    }
  }, []);

  const connect = useCallback(() => {
    // Always tear down any existing socket before opening a new one
    disconnect();

    if (!mountedRef.current || !symbolsKey) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not authenticated');
      return;
    }

    const params = new URLSearchParams({
      symbols: symbolsKey,
      token,
      interval: String(pollInterval),
      ...(includeIndicators ? { include_indicators: 'true' } : {}),
    });

    const url = `${API_WS_BASE}/api/v1/market/ws/prices?${params}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (wsRef.current !== ws) return; // stale socket
      setConnected(true);
      setError(null);
      retryRef.current = 0;
    };

    ws.onmessage = (event) => {
      if (wsRef.current !== ws) return; // stale socket
      try {
        const msg = JSON.parse(event.data as string);
        if (msg.type === 'price_update') {
          const tick: MarketTick = {
            symbol: msg.symbol,
            price: msg.price ?? 0,
            bid: msg.bid,
            ask: msg.ask,
            change: msg.change ?? 0,
            change_pct: msg.change_pct ?? 0,
            timestamp: msg.timestamp ?? new Date().toISOString(),
          };
          // Buffer the tick — flush to React state at most once per TICK_FLUSH_INTERVAL_MS.
          // This coalesces rapid FOREX/CC bursts so the DOM only re-renders periodically,
          // eliminating the rapid visual flashing without losing any price data.
          pendingTicksRef.current[msg.symbol] = tick;
          if (!flushTimerRef.current) {
            flushTimerRef.current = setTimeout(flushPendingTicks, TICK_FLUSH_INTERVAL_MS);
          }
        } else if (msg.type === 'indicators_update') {
          const snap: IndicatorSnapshot = {
            symbol: msg.symbol,
            rsi_14: msg.rsi_14,
            sma_50: msg.sma_50,
            sma_200: msg.sma_200,
            cached_at: msg.cached_at,
          };
          setIndicators((prev) => ({ ...prev, [msg.symbol]: snap }));
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onerror = () => {
      if (wsRef.current !== ws) return; // stale socket
      setError('WebSocket connection error');
    };

    ws.onclose = (event) => {
      if (wsRef.current !== ws) return; // stale socket — cleanup already handled it
      wsRef.current = null;
      setConnected(false);

      if (!mountedRef.current) return; // unmounted — do not reconnect

      if (event.code === 4001) {
        setError('Authentication failed — please log in again');
        return; // do not reconnect on auth failure
      }

      const delay = BACKOFF_DELAYS[Math.min(retryRef.current, BACKOFF_DELAYS.length - 1)];
      retryRef.current += 1;
      retryTimerRef.current = setTimeout(connect, delay);
    };
  }, [symbolsKey, includeIndicators, pollInterval, disconnect, flushPendingTicks]);

  useEffect(() => {
    mountedRef.current = true;
    retryRef.current = 0;
    connect();

    return () => {
      mountedRef.current = false;
      disconnect();
      setConnected(false);
      retryRef.current = 0;
    };
  }, [connect, disconnect]);

  return { ticks, indicators, connected, error };
}

export default useMarketWS;
